import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { scooterApi, formatPrice, getFileUrl } from "@/lib/api";
import { 
  Gauge,
  Calendar,
  Phone,
  MessageCircle,
  MapPin,
  ArrowRight,
  Sparkles,
  Shield,
  CheckCircle,
  Star,
  ExternalLink
} from "lucide-react";

// Contact info
const CONTACT_INFO = {
  phone: "0792250058",
  whatsapp: "40792250058",
  location: "Bucuresti, Romania"
};

// Generate WhatsApp link
const generateWhatsAppLink = (scooter = null) => {
  let message;
  if (scooter) {
    message = `Buna! Sunt interesat de scuterul ${scooter.brand} ${scooter.model} ${scooter.year} (${formatPrice(scooter.selling_price)}).`;
  } else {
    message = `Buna! Sunt interesat de scuterele voastre.`;
  }
  return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
};

export default function PromoPage() {
  const [scooters, setScooters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set Open Graph meta tags dynamically
    document.title = "OFERTE SCUTERE Premium - Scutersh | Preturi de la 400€";
    
    // Update meta tags for Facebook
    const updateMetaTag = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('og:title', 'OFERTE SCUTERE Premium - Preturi de la 400€');
    updateMetaTag('og:description', 'Scutere second-hand verificate si garantate. Livrare in toata Romania. Contacteaza-ne pentru oferte!');
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:url', window.location.href);
    
    fetchScooters();
  }, []);

  const fetchScooters = async () => {
    try {
      const data = await scooterApi.getShowroom({ status: "in_stock" });
      // Filter out scooters with no valid price, sort by price (lowest first), get top 12
      const withPrice = data.filter(s => s.selling_price && s.selling_price > 0);
      const sorted = withPrice.sort((a, b) => a.selling_price - b.selling_price).slice(0, 12);
      setScooters(sorted);
      
      // Set first scooter image as OG image
      if (sorted.length > 0 && sorted[0].photos?.length > 0) {
        const imageUrl = `${window.location.origin}/api/uploads/${sorted[0].photos[0].replace('/api/uploads/', '').replace('/uploads/', '')}`;
        const metaTag = document.querySelector('meta[property="og:image"]');
        if (metaTag) metaTag.setAttribute('content', imageUrl);
      }
    } catch (error) {
      console.error("Failed to fetch scooters");
    } finally {
      setLoading(false);
    }
  };

  const defaultImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop";

  // Get lowest price (only from valid priced scooters)
  const lowestPrice = scooters.length > 0 
    ? Math.min(...scooters.filter(s => s.selling_price > 0).map(s => s.selling_price))
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Se incarca ofertele...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="promo-page" className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <div data-testid="promo-hero" className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23BEF264' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-20">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-black text-white mb-2">
              SCUTERSH
            </h1>
            <p className="text-white/60 text-lg">Scutere Premium Second-Hand</p>
          </div>

          {/* Main Offer */}
          <div className="text-center mb-12">
            <div className="inline-block bg-primary/20 border border-primary/50 rounded-2xl px-8 py-6 mb-6">
              <p className="text-primary text-sm font-bold uppercase tracking-wider mb-2">
                Oferte Speciale
              </p>
              <p className="text-5xl md:text-7xl font-black text-white">
                {lowestPrice ? (
                  <>de la <span className="text-primary">{lowestPrice}€</span></>
                ) : (
                  <span className="text-primary">Preturi Imbatabile</span>
                )}
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-white/80">
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Shield className="w-4 h-4 text-primary" />
                Verificate Tehnic
              </span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Garantie Inclusa
              </span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-blue-400" />
                Livrare Romania
              </span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
              <Button data-testid="promo-whatsapp-hero" size="lg" className="bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-6 w-full sm:w-auto">
                <MessageCircle className="w-6 h-6 mr-2" />
                WhatsApp: {CONTACT_INFO.phone}
              </Button>
            </a>
            <a href={`tel:${CONTACT_INFO.phone}`}>
              <Button data-testid="promo-call-hero" size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="w-6 h-6 mr-2" />
                Suna Acum
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Scooters Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-white mb-2">
            <Sparkles className="w-8 h-8 inline mr-2 text-primary" />
            Scutere Disponibile
          </h2>
          <p className="text-white/60">{scooters.length} scutere in stoc</p>
        </div>

        {scooters.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/60 text-xl">Momentan nu avem scutere in stoc.</p>
            <p className="text-white/40 mt-2">Contacteaza-ne pentru comenzi speciale!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {scooters.map((scooter) => (
              <Card 
                key={scooter.scooter_id} 
                data-testid={`promo-scooter-card-${scooter.scooter_id}`}
                className="bg-white/5 border-white/10 hover:border-primary/50 transition-all duration-300 overflow-hidden group"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={scooter.photos?.[0] ? getFileUrl(scooter.photos[0]) : defaultImage}
                    alt={`${scooter.brand} ${scooter.model}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Price Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-primary text-black font-bold text-lg px-3 py-1">
                      {formatPrice(scooter.selling_price)}
                    </Badge>
                  </div>
                  {/* Year Badge */}
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="bg-black/70 text-white border-white/30">
                      {scooter.year}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-4">
                  {/* Title */}
                  <h3 className="text-white font-bold text-lg mb-2">
                    {scooter.brand} {scooter.model}
                  </h3>

                  {/* Specs */}
                  <div className="flex items-center gap-4 text-white/60 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Gauge className="w-4 h-4" />
                      {scooter.mileage.toLocaleString()} km
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {scooter.engine_cc} cc
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <a 
                      href={generateWhatsAppLink(scooter)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button size="sm" className="w-full bg-green-500 hover:bg-green-600 text-white">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                    </a>
                    <Link to={`/scooter/${scooter.scooter_id}`} className="flex-1">
                      <Button size="sm" variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">
                        Detalii
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link to="/showroom">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-black font-bold">
              <ExternalLink className="w-5 h-5 mr-2" />
              Vezi Toate Scuterele
            </Button>
          </Link>
        </div>
      </div>

      {/* Trust Section */}
      <div className="bg-white/5 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white mb-2">De Ce Sa Alegi Scutersh?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Verificate 100%</h3>
              <p className="text-white/60">Fiecare scuter este verificat tehnic complet inainte de vanzare</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Transparenta Totala</h3>
              <p className="text-white/60">Istoric complet, reparatii, km reali - totul documentat</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Garantie</h3>
              <p className="text-white/60">Garantie comerciala inclusa pentru linistea ta</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Interesat? Contacteaza-ne!
          </h2>
          <p className="text-white/60 mb-8">
            Raspundem rapid la orice intrebare. Livrare in toata Romania.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white font-bold text-xl px-10 py-7 w-full sm:w-auto">
                <MessageCircle className="w-7 h-7 mr-3" />
                WhatsApp
              </Button>
            </a>
            <a href={`tel:${CONTACT_INFO.phone}`}>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-black font-bold text-xl px-10 py-7 w-full sm:w-auto">
                <Phone className="w-7 h-7 mr-3" />
                {CONTACT_INFO.phone}
              </Button>
            </a>
          </div>
          
          <p className="text-white/40 mt-8 text-sm">
            <MapPin className="w-4 h-4 inline mr-1" />
            {CONTACT_INFO.location}
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10 py-6">
        <p className="text-center text-white/40 text-sm">
          © {new Date().getFullYear()} Scutersh. Toate drepturile rezervate.
        </p>
      </div>
    </div>
  );
}
